import { compareByPriorityThenDue, inChannel } from "./tasksModel";

// Messages → Tasks board: the four columns, what each holds, and what
// dragging a card from one to another does. Pure — TaskBoard renders it.
// The board has no "Assigned to me / by me" or "Show completed" filter (those
// are the list's): it holds everything assigned to or by the user — or, for a
// channel picked in the Tasks tab, everything assigned to and by its members.

export const BOARD_COLUMNS = [
  { id: "open", label: "Open" },
  { id: "in_progress", label: "In progress" },
  { id: "in_review", label: "In review" },
  { id: "done", label: "Completed" },
];

export const RECENT_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

// When an item was completed (or otherwise closed), for the Completed column.
export function closedAt(item) {
  return item.completedAt || item.updatedAt || item.createdAt || null;
}

const closedTime = (item) => Date.parse(closedAt(item) || "") || 0;

// The column an item sits in for this user. A rejected item waits on the
// person who assigned it (Open for them, and for anyone else looking on); for
// the person who rejected it, it's closed.
export function columnOf(item, userId) {
  if (item.done || item.cancelled) return "done";
  if (item.rejected) return item.assignedTo === userId && item.assignedBy !== userId ? "done" : "open";
  if (item.status === "in_progress") return "in_progress";
  if (item.status === "pending_review") return "in_review";
  return "open";
}

// Where an item goes on this user's board, or null when it isn't on it: not
// theirs (for a channel, memberIds: not assigned to and by its members), or closed
// and not a completion from the last RECENT_DAYS days (older completions,
// cancelled items and what they rejected are in the list, with Show completed).
export function boardColumnFor(item, userId, now = new Date(), { memberIds } = {}) {
  const onBoard = memberIds
    ? inChannel(item, memberIds)
    : item.assignedTo === userId || item.assignedBy === userId;
  if (!onBoard) return null;
  const column = columnOf(item, userId);
  if (column !== "done") return column;
  return item.done && closedTime(item) >= now.getTime() - RECENT_DAYS * DAY_MS ? "done" : null;
}

// { open, in_progress, in_review, done } for this user; Completed most
// recent first.
export function boardColumns(items, { userId, now = new Date(), memberIds }) {
  const columns = { open: [], in_progress: [], in_review: [], done: [] };
  const members = memberIds ? new Set(memberIds) : null;
  for (const item of items || []) {
    const column = boardColumnFor(item, userId, now, { memberIds: members });
    if (column) columns[column].push(item);
  }
  ["open", "in_progress", "in_review"].forEach((id) => columns[id].sort(compareByPriorityThenDue));
  columns.done.sort((a, b) => closedTime(b) - closedTime(a));
  return columns;
}

// The side of a card the user is on, for its list row: "mine" when it's
// assigned to them, "assigned" when they handed it to someone, "others"
// when they're only looking on (a channel's tasks).
export function viewFor(item, userId) {
  if (item.assignedTo === userId) return "mine";
  return item.assignedBy === userId ? "assigned" : "others";
}

// What dropping `item` on column `to` does for this user: { action: "move",
// to } (the server's board move), { reason } when it can't go there, or {}
// when it was dropped where it already is. The person who assigned it can
// move it to any column; the person it's assigned to to any column but
// Completed (that stays the assigner's call). The server checks the same
// (task_review.move_status). A rejected item waits on Reassign.
export function boardMove(item, to, userId) {
  if (columnOf(item, userId) === to) return {};
  if (item.rejected) return { reason: "It was rejected: use Reassign on the card" };
  const assignee = item.assignedTo === userId;
  // With nobody recorded as the assigner, the assignee counts as one.
  const assigner = item.assignedBy ? item.assignedBy === userId : assignee;
  if (!assignee && !assigner) return { reason: "That move isn't available to you" };
  if (to === "in_progress" && item.kind === "order") return { reason: "Orders don't have an In progress step" };
  if (to === "done" && !assigner) {
    return { reason: `Only ${item.assignerName || "the person who assigned it"} can mark it completed` };
  }
  return { action: "move", to };
}
